import React, { useEffect, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Card,
} from '../AumhumSite/sections'
import { ArrowRight, MessageCircle, PhoneCall } from '../AumhumSite/icons'
import StepDots from './StepDots'
import LegalDrawer from './LegalDrawer'
import {
  sendProspectOtp,
  verifyProspectOtp,
  signupPatientProspect,
  isLoggedIn,
  getStoredAuthorities,
} from 'src/consumer/services/patientProspectService'
import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'

// Step 3: account creation. We collect just enough pre-OTP for the care
// team to start matching: subject (self vs someone else), name(s), mobile
// (E.164 via react-phone-input-2 — full ITU country coverage with a
// searchable dropdown), email (optional), age, gender. The OTP confirms
// identity; the rest is sent through to /initialize so the support
// dashboard sees real names and demographics on first load instead of a
// blank prospect record.
//
// Mobile is the only mandatory field (plus consent + OTP). Everything
// else is genuinely optional — the user can always fill more on
// /care/status's progressive intake form.
//
// Mobile widget: react-phone-input-2 (same as /signup) gives us flag +
// dial-code dropdown + inline national formatting + search across all
// 240+ ITU country codes. India is preferred-first (and pre-selected)
// because AUMHUM serves primarily India + a few diaspora corridors,
// but every country is reachable via the search box.

const SUBJECT_OPTIONS = [
  { id: 'SELF', label: 'For myself' },
  { id: 'DEPENDENT', label: 'For someone else' },
]

const GENDER_OPTIONS = [
  { id: 'FEMALE', label: 'Female' },
  { id: 'MALE', label: 'Male' },
  { id: 'OTHER', label: 'Other' },
  { id: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
]

export default function MobileOtpStep({ resolvedPathway, onSignedUp, onBack, onChangePathway, intent = null }) {
  // Booking-funnel framing — the explainer copy below switches when
  // ?intent=consultation is set so visitors who came in via
  // /book-consultation see "Verify and book" rather than the broader
  // "verify and create your account" copy.
  const isConsultation = intent === 'consultation'
  const [subjectType, setSubjectType] = useState('SELF')
  const [name, setName] = useState('')
  const [subjectName, setSubjectName] = useState('')
  // E.164 digits-only WITHOUT leading + (react-phone-input-2 convention).
  // We prepend the '+' when sending to the backend.
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('')

  const [otp, setOtp] = useState('')
  const [channel, setChannel] = useState('whatsapp')
  const [sessionId, setSessionId] = useState(null)
  const [otpSent, setOtpSent] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  // Rate-limit countdown surfaced from the backend's 429 response.
  // `rateLimitedFor` is the seconds remaining the user has to wait
  // before they're allowed to request another OTP. We tick this down
  // every second so the error message stays live ("Try again in 22s",
  // "21s", …) instead of going stale the moment we render it.
  const [rateLimitedFor, setRateLimitedFor] = useState(0)
  const rateLimitTickRef = useRef(null)
  // null when closed, 'terms' | 'privacy' when LegalDrawer is open.
  const [legalDrawer, setLegalDrawer] = useState(null)
  const tickRef = useRef(null)
  // Refs let us scroll the just-revealed OTP block and the consent
  // block into view + grab focus after the OTP send succeeds, so the
  // user clearly sees "next thing to do".
  const otpInputRef = useRef(null)
  const consentRef = useRef(null)

  useEffect(() => {
    if (resendIn <= 0) {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
      return undefined
    }
    tickRef.current = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [resendIn])

  // The OTP card + consent box only mount after the WhatsApp code is
  // sent. As soon as that happens, scroll the new section into view
  // and put focus on the OTP input — without this nudge the user has
  // to manually scroll past the (now-locked) profile fields to find
  // the next action, which previously caused drop-off.
  useEffect(() => {
    if (!otpSent) return
    const t = setTimeout(() => {
      try {
        if (otpInputRef.current) {
          otpInputRef.current.focus({ preventScroll: true })
          otpInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      } catch {
        // older browsers without smooth-scroll: ignore
      }
    }, 80)
    return () => clearTimeout(t)
  }, [otpSent])

  // Live tick-down for the rate-limit error. Whenever the user trips
  // the backend's "too many OTP requests" guard, we set
  // rateLimitedFor=N (seconds) and this effect counts it down to 0.
  // The error message reads the current value on each render so the
  // user sees a real countdown rather than a static "try later".
  useEffect(() => {
    if (rateLimitedFor <= 0) {
      if (rateLimitTickRef.current) clearInterval(rateLimitTickRef.current)
      rateLimitTickRef.current = null
      return undefined
    }
    rateLimitTickRef.current = setInterval(
      () => setRateLimitedFor((s) => Math.max(0, s - 1)),
      1000,
    )
    return () => {
      if (rateLimitTickRef.current) clearInterval(rateLimitTickRef.current)
    }
  }, [rateLimitedFor])

  // react-phone-input-2 emits digits-only without the leading +. The
  // wellness OTP backend expects "+digits" so we add the prefix when
  // sending. isValidPhoneNumber (libphonenumber-js) gives us strict
  // per-country E.164 validation rather than the previous permissive
  // 6–15 digit regex.
  const fullMobile = mobile ? `+${mobile}` : ''
  const validMobile = !!mobile && isValidPhoneNumber(fullMobile)

  // Trivial e-mail sanity check — the backend doesn't require email and
  // typos here are harmless, but flagging an obviously broken value
  // saves the user a round-trip.
  const emailLooksOk = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  // Age sanity: integer 0..120. Empty = optional, treat as null.
  const ageIsValid = !age || (/^\d{1,3}$/.test(age) && Number(age) >= 0 && Number(age) <= 120)

  const requestOtp = async (preferredChannel) => {
    setError('')
    if (!validMobile) {
      setError('Please enter a valid mobile number for the country you picked.')
      return
    }
    if (!emailLooksOk) {
      setError('That email doesn’t look right. Leave it blank if you’d rather skip.')
      return
    }
    if (!ageIsValid) {
      setError('Please enter a valid age (0–120) or leave it blank.')
      return
    }
    // Refuse to re-attempt while the backend's rate-limit window is
    // still active — the network call would just be rejected with the
    // same 429 and reset the countdown. The button is also visually
    // disabled while this is non-zero, so this is belt-and-braces.
    if (rateLimitedFor > 0) {
      return
    }
    try {
      setSubmitting(true)
      // Run the v3 captcha challenge BEFORE the network call so bots
      // are dropped before we burn Twilio quota. Returns null on hosts
      // without a configured site-key (dev / preview); backend treats
      // a null token as trivially successful in that case.
      const recaptchaToken = await executeRecaptchaV3('signup_otp_send')
      const res = await sendProspectOtp(fullMobile, preferredChannel, { recaptchaToken })
      setSessionId(res.sessionId)
      setChannel(res.channel)
      setOtpSent(true)
      setResendIn(res.resendDelaySeconds || 20)
    } catch (e) {
      // Rate-limit gets special treatment: store the seconds-remaining
      // so the error message ticks down live. The plain message stays
      // around for non-rate-limit errors (network blip, captcha fail,
      // etc.) — those don't carry a retry window.
      const ra =
        Number.isFinite(e?.retryAfter) && e.retryAfter > 0
          ? Math.ceil(e.retryAfter)
          : 0
      if (ra > 0 || e?.code === 'RATE_LIMIT_EXCEEDED') {
        // Fall back to 30s when the backend returned the code but no
        // explicit retry window — better than 0 (which would let the
        // user re-press immediately and just hit the same wall).
        setRateLimitedFor(ra > 0 ? ra : 30)
        setError('') // rate-limit message is rendered separately, live-ticking
      } else {
        setError(e.message || 'Could not send the code. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const verifyAndSignup = async () => {
    setError('')
    if (!consent) {
      setError('Please agree to the privacy + terms before continuing.')
      return
    }
    setSubmitting(true)
    let verify
    try {
      verify = await verifyProspectOtp({ mobile: fullMobile, otp, sessionId, channel })
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
      return
    }

    try {
      const session = await signupPatientProspect({
        name: name.trim() || null,
        mobile: fullMobile,
        email: email.trim() || null,
        otpToken: verify.otpToken,
        pathway: resolvedPathway?.pathway,
        specialtyTag: resolvedPathway?.specialtyTag,
        subjectType,
        subjectName: subjectType !== 'SELF' && subjectName.trim() ? subjectName.trim() : null,
        age: age ? Number(age) : null,
        gender: gender || null,
        consents: { tos: true, privacy: true },
        source: 'WEB',
        bookingIntent: intent,
      })
      onSignedUp?.(session)
    } catch (e) {
      // Initialize failed but the OTP step did sign the user in. Hand off
      // to /care/status with a hard nav so the auth-aware shell reloads
      // (header, login button), and surface the failure reason on the
      // landing page rather than stranding the user on this form.
      //
      // If the caller is already a ROLE_PATIENT, /care/status is the
      // wrong fallback — that's the prospect dashboard. Land them on
      // /patient-home with the same failure reason instead so they see
      // a familiar surface.
      if (isLoggedIn()) {
        const reason = e?.message || 'init_failed'
        const auths = getStoredAuthorities()
        if (Array.isArray(auths) && auths.includes('ROLE_PATIENT')) {
          window.location.assign(
            `/patient-home?careRequestFailed=1&reason=${encodeURIComponent(reason)}`,
          )
          return
        }
        window.location.assign(
          `/care/status?initFailed=1&reason=${encodeURIComponent(reason)}`,
        )
        return
      }
      setError(e.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  const isForSomeoneElse = subjectType !== 'SELF'

  return (
    <Container className="pps-shell">
      <StepDots step={3} of={3} />
      <Eyebrow>Last step</Eyebrow>
      <SectionTitle as="h1">Create your free account</SectionTitle>

      {/* Sets expectations BEFORE the user starts typing: this step
          verifies mobile + creates the account; the deeper intake
          (history, reports, etc.) lives on the next page. Booking
          variant tightens the language toward "let's book". */}
      <p className="pps-otp-explainer">
        {isConsultation
          ? 'Verifying your mobile creates your free AUMHUM account. After that you’ll share a few details so our care team can match you to the right specialist and book your consultation.'
          : 'Verifying your mobile creates your free AUMHUM account. On the next page you can share more details — your story, any reports, what’s been going on — so our care team can match you with the right specialist.'}
      </p>

      {resolvedPathway ? (
        <div className="pps-recap pps-recap--card">
          <div className="pps-recap-meta">
            <span className="pps-recap-kicker">You&rsquo;re booking for</span>
            <span className="pps-recap-pill">
              {resolvedPathway.topLabel}
              {resolvedPathway.subLabel ? <> · {resolvedPathway.subLabel}</> : null}
            </span>
          </div>
          {onChangePathway ? (
            <button
              type="button"
              className="pps-recap-change"
              onClick={() => {
                if (otpSent) {
                  // Confirm before nuking an in-flight OTP session — the
                  // user would have to re-request a new code anyway.
                  const ok = window.confirm(
                    'Change the type of care? You’ll need to send a new WhatsApp code after.',
                  )
                  if (!ok) return
                }
                onChangePathway()
              }}
              disabled={submitting}
            >
              Change
            </button>
          ) : null}
        </div>
      ) : null}

      <Card className="pps-card-form" emphasised>
        {/* Who is this care for — drives whether subsequent age/gender
            fields refer to the user or to a dependent. */}
        <div className="pps-field">
          <span className="pps-field-label">Who is this care request for?</span>
          <div className="pps-chip-row" role="radiogroup" aria-label="Care request is for">
            {SUBJECT_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={subjectType === opt.id}
                className={`pps-chip${subjectType === opt.id ? ' is-active' : ''}`}
                onClick={() => setSubjectType(opt.id)}
                disabled={otpSent && !error}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-name">
            Your name <span className="pps-field-optional">(optional)</span>
          </label>
          <input
            id="pps-name"
            className="pps-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name is fine"
            autoComplete="given-name"
            disabled={otpSent && !error}
          />
        </div>

        {isForSomeoneElse ? (
          <div className="pps-field">
            <label className="pps-field-label" htmlFor="pps-subject-name">
              Their name <span className="pps-field-optional">(the person who needs care)</span>
            </label>
            <input
              id="pps-subject-name"
              className="pps-input"
              type="text"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              placeholder="First name is fine"
              autoComplete="off"
              disabled={otpSent && !error}
            />
          </div>
        ) : null}

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-mobile">Mobile number</label>
          {/*
            react-phone-input-2 with search enabled — full ITU country
            coverage, not just the 12-entry shortlist we used to show.
            Reuses the same `.sv-phoneinput-wrap` styling /signup uses
            so the two surfaces look identical. `preferredCountries`
            keeps the India / diaspora corridors at the top of the
            dropdown for fast picking, but every country is reachable
            via the search box.
          */}
          <div className="sv-phoneinput-wrap">
            <PhoneInput
              inputProps={{ id: 'pps-mobile', name: 'mobile', autoComplete: 'tel' }}
              country={'in'}
              preferredCountries={['in', 'us', 'gb', 'ae', 'sg', 'au', 'ca']}
              value={mobile}
              onChange={(v) => setMobile(v)}
              enableSearch
              disabled={otpSent && !error}
              inputStyle={{ width: '100%' }}
              containerStyle={{ width: '100%' }}
              dropdownStyle={{ zIndex: 99999 }}
            />
          </div>
          <span className="pps-help">
            We&rsquo;ll send a one-time code on WhatsApp. Standard rates may apply.
          </span>
        </div>

        <div className="pps-field">
          <label className="pps-field-label" htmlFor="pps-email">
            Email <span className="pps-field-optional">(optional)</span>
          </label>
          <input
            id="pps-email"
            className="pps-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={otpSent && !error}
          />
          <span className="pps-help">
            For appointment confirmations and care summaries. We don&rsquo;t spam.
          </span>
        </div>

        <div className="pps-field-row">
          <div className="pps-field pps-field--half">
            <label className="pps-field-label" htmlFor="pps-age">
              {isForSomeoneElse ? 'Their age' : 'Your age'}{' '}
              <span className="pps-field-optional">(optional)</span>
            </label>
            <input
              id="pps-age"
              className="pps-input"
              type="number"
              min={0}
              max={120}
              value={age}
              onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 32"
              autoComplete="off"
              inputMode="numeric"
              disabled={otpSent && !error}
            />
          </div>

          <div className="pps-field pps-field--half">
            <span className="pps-field-label">
              {isForSomeoneElse ? 'Their gender' : 'Your gender'}{' '}
              <span className="pps-field-optional">(optional)</span>
            </span>
            <div className="pps-chip-row" role="radiogroup" aria-label="Gender">
              {GENDER_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={gender === opt.id}
                  className={`pps-chip pps-chip--small${gender === opt.id ? ' is-active' : ''}`}
                  onClick={() => setGender((cur) => (cur === opt.id ? '' : opt.id))}
                  disabled={otpSent && !error}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!otpSent ? (
          <>
            {/* Pre-send rate-limit notice. If the very first
                "Send WhatsApp code" press hits the backend's 429,
                otpSent stays false but we still want a live countdown
                under the button so the user knows when they can
                retry. Hidden when not rate-limited. */}
            {rateLimitedFor > 0 ? (
              <div
                className="pps-rate-limited pps-rate-limited--pre-send"
                role="status"
                aria-live="polite"
              >
                Too many OTP requests. You can try again in{' '}
                <strong>{rateLimitedFor}s</strong>.
              </div>
            ) : null}
            <div className="pps-cta-row">
              <button type="button" className="pps-btn is-ghost" onClick={onBack}>Back</button>
              <button
                type="button"
                className={`pps-btn is-primary${validMobile && !submitting && rateLimitedFor === 0 ? '' : ' is-disabled'}`}
                disabled={!validMobile || submitting || rateLimitedFor > 0}
                onClick={() => requestOtp('whatsapp')}
              >
                {submitting
                  ? 'Sending…'
                  : rateLimitedFor > 0
                    ? `Try again in ${rateLimitedFor}s`
                    : (<>Send WhatsApp code <MessageCircle /></>)}
              </button>
            </div>
          </>
        ) : (
          <>
            {/*
              OTP card — visually elevated so the user immediately sees
              "this is the next thing to do". Step number badge,
              accent border, large centred OTP input. The element gets
              scrolled into view + focused via the effect above as soon
              as otpSent flips to true.
            */}
            <div className="pps-otp-focus" role="group" aria-labelledby="pps-otp-heading">
              <div className="pps-step-badge" aria-hidden="true">Step 1 of 2</div>
              <h2 id="pps-otp-heading" className="pps-otp-heading">
                Enter the code we just sent
              </h2>
              <p className="pps-otp-sub">
                Check your {channel === 'sms' ? 'SMS' : 'WhatsApp'} for a 4–6 digit code from AUMHUM.
              </p>

              <div className="pps-otp-row">
                <input
                  id="pps-otp"
                  ref={otpInputRef}
                  className="pps-input pps-otp-input"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  /* Accept up to 8 digits — production OTPs are always
                     6 (the WhatsApp auth generator emits 6), but the
                     CC test-OTP-mobile whitelist lets operators set
                     OTPs anywhere in [4..8] digits. The previous
                     6-cap was silently truncating 7- and 8-digit test
                     OTPs and locking testers out of /care/get-started. */
                  maxLength={8}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  /* 6 dots = the production OTP length. Test mobiles
                     can set 4-8; testers know their own length so the
                     placeholder doesn't need to reflect every case. */
                  placeholder="••••••"
                  autoComplete="one-time-code"
                  aria-describedby="pps-otp-resend"
                />
              </div>

              <div id="pps-otp-resend" className="pps-channel-toggle">
                {rateLimitedFor > 0 ? (
                  <span className="pps-rate-limited" role="status" aria-live="polite">
                    Too many OTP requests. You can try again in{' '}
                    <strong>{rateLimitedFor}s</strong>.
                  </span>
                ) : resendIn > 0 ? (
                  <span>Resend in {resendIn}s</span>
                ) : (
                  <button type="button" onClick={() => requestOtp(channel)}>Resend code</button>
                )}
              </div>
            </div>

            {/*
              Consent block — also visually elevated so the user clearly
              sees that ticking this is required before "Create account".
              Terms / Privacy are real buttons that open <LegalDrawer>
              inline — no tab-yank, no localStorage replay games.
            */}
            <div
              ref={consentRef}
              className={`pps-consent-focus${consent ? ' is-checked' : ''}`}
            >
              <div className="pps-step-badge" aria-hidden="true">Step 2 of 2</div>
              <label htmlFor="pps-consent" className="pps-consent-row">
                <input
                  id="pps-consent"
                  type="checkbox"
                  className="pps-consent-check"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span className="pps-consent-text">
                  I agree to AUMHUM&rsquo;s{' '}
                  <button
                    type="button"
                    className="pps-legal-link"
                    onClick={() => setLegalDrawer('privacy')}
                  >
                    Privacy Policy
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    className="pps-legal-link"
                    onClick={() => setLegalDrawer('terms')}
                  >
                    Terms of Service
                  </button>
                  , and to share these details so the care team can find me a specialist.
                </span>
              </label>
              {!consent ? (
                <div className="pps-consent-hint" role="note">
                  Tick the box above to enable &ldquo;Create account&rdquo;.
                </div>
              ) : null}
            </div>

            <div className="pps-cta-row">
              <button type="button" className="pps-btn is-ghost" onClick={onBack}>Back</button>
              <button
                type="button"
                className={`pps-btn is-primary${otp.length >= 4 && consent && !submitting ? '' : ' is-disabled'}`}
                disabled={otp.length < 4 || !consent || submitting}
                onClick={verifyAndSignup}
              >
                {submitting ? 'Creating…' : (<>Create account <ArrowRight /></>)}
              </button>
            </div>
          </>
        )}

        {error ? <div className="pps-error" role="alert">{error}</div> : null}

        <div className="pps-secondary-row">
          <a className="pps-btn is-ghost" href="https://wa.me/918047492952" target="_blank" rel="noreferrer">
            <PhoneCall /> Just want to talk? Chat on WhatsApp
          </a>
        </div>
      </Card>

      <LegalDrawer
        open={legalDrawer !== null}
        type={legalDrawer}
        onClose={() => setLegalDrawer(null)}
        onAccept={() => setConsent(true)}
      />
    </Container>
  )
}
